import _extends from '@babel/runtime/helpers/extends';
import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import _objectWithoutProperties from '@babel/runtime/helpers/objectWithoutProperties';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import * as React from 'react';
import { useDirectionContext, Box } from '@drone-js/core';
import { format, differenceInCalendarMonths } from 'date-fns';
import { DayPicker as DayPicker$1 } from 'react-day-picker';
import { getNumberOfMonths, getCustomTabbableDate } from './DatePicker.mappers.js';
import { getLabelMapKey, DayButton, DayCell, Month, Months, MonthGrid, MobileCaption, MonthLabel, Caption, WeekRow, WeekBody, WeekDayHeader, WeekDaysRow, RootContainer } from './DatePicker.styled.js';
import { removeDefaultTableStyles } from './DatePickerStyles.js';
import { getStartingDayOfWeekNumber } from './util/index.js';

var _excluded = ["date", "dateRange", "disabledDates", "id", "isSingleDate", "isLoading", "loadingLabel", "onDateRangeSelect", "onDateSelect", "onMonthChange", "getDateAriaLabel", "rangeMax", "rangeMin", "startDate", "endDate", "variant", "scrollableMonthCount", "month", "monthYearPicker", "onCalendarChange", "locale", "currentDate", "headingTag", "today"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
var DEFAULT_SCROLLABLE_MONTH_COUNT = 13;
var mapDateToLabel = function mapDateToLabel(labels) {
  return labels.reduce(function (acc, label) {
    return _objectSpread(_objectSpread({}, acc), {}, _defineProperty({}, getLabelMapKey(label.date), label));
  }, {});
};
var DayPicker = /*#__PURE__*/React.forwardRef(function (props, ref) {
  var _props$labels;
  var date = props.date,
    dateRange = props.dateRange,
    _props$disabledDates = props.disabledDates,
    disabledDates = _props$disabledDates === void 0 ? [] : _props$disabledDates,
    id = props.id,
    isSingleDate = props.isSingleDate,
    isLoading = props.isLoading,
    loadingLabel = props.loadingLabel,
    onDateRangeSelect = props.onDateRangeSelect,
    onDateSelect = props.onDateSelect,
    onMonthChange = props.onMonthChange,
    getDateAriaLabel = props.getDateAriaLabel,
    rangeMax = props.rangeMax,
    rangeMin = props.rangeMin,
    startDate = props.startDate,
    endDate = props.endDate,
    variant = props.variant,
    _props$scrollableMont = props.scrollableMonthCount,
    scrollableMonthCount = _props$scrollableMont === void 0 ? DEFAULT_SCROLLABLE_MONTH_COUNT : _props$scrollableMont,
    month = props.month,
    monthYearPicker = props.monthYearPicker,
    onCalendarChange = props.onCalendarChange,
    locale = props.locale,
    currentDate = props.currentDate,
    headingTag = props.headingTag,
    today = props.today,
    rest = _objectWithoutProperties(props, _excluded);
  var direction = useDirectionContext();
  var isScrollable = variant === 'scrollable';
  var numberOfMonths = getNumberOfMonths(variant, scrollableMonthCount, startDate, endDate);
  var startingDayOfWeek = getStartingDayOfWeekNumber(locale);
  var hasLabels = !!((_props$labels = props.labels) !== null && _props$labels !== void 0 && _props$labels.length) || !!isLoading;
  var labelMap = props.labels ? mapDateToLabel(props.labels) : undefined;
  var formatWeekdayName = function formatWeekdayName(weekday) {
    return format(new Date(weekday), 'EEEEEE', {
      locale: locale
    });
  };
  var dayPickerProps = {
    startMonth: startDate,
    modifiers: {
      customTabbable: getCustomTabbableDate(month, currentDate, date)
    },
    disabled: [].concat(_toConsumableArray(startDate ? [{
      before: startDate
    }] : []), _toConsumableArray(endDate ? [{
      after: endDate
    }] : []), _toConsumableArray(Array.isArray(disabledDates) ? disabledDates : [disabledDates])),
    numberOfMonths: numberOfMonths,
    dir: direction,
    locale: locale,
    formatters: {
      formatWeekdayName: formatWeekdayName
    },
    components: {
      DayButton: function DayButton$1(prop) {
        return /*#__PURE__*/React.createElement(DayButton, _extends({
          today: today,
          dir: direction
        }, prop));
      },
      Day: function Day(prop) {
        return /*#__PURE__*/React.createElement(DayCell, _extends({
          hasPriceLabel: hasLabels,
          isScrollable: isScrollable,
          isLoading: isLoading,
          priceLabelMap: labelMap,
          loadingLabel: loadingLabel,
          getDateAriaLabel: getDateAriaLabel
        }, prop));
      },
      Month: function Month$1(prop) {
        return /*#__PURE__*/React.createElement(Month, _extends({
          isScrollable: isScrollable
        }, prop));
      },
      Months: function Months$1(prop) {
        return /*#__PURE__*/React.createElement(Months, _extends({
          isScrollable: isScrollable
        }, prop));
      },
      MonthGrid: function MonthGrid$1(prop) {
        return /*#__PURE__*/React.createElement(MonthGrid, prop);
      },
      MonthCaption: function MonthCaption(prop) {
        if (isScrollable) return /*#__PURE__*/React.createElement(MobileCaption, prop, /*#__PURE__*/React.createElement(MonthLabel, {
          onMonthChange: onMonthChange,
          headingTag: headingTag,
          calendarMonth: prop.calendarMonth,
          onCalendarChange: onCalendarChange,
          monthYearPicker: monthYearPicker,
          locale: locale,
          isScrollable: isScrollable
        }));
        return /*#__PURE__*/React.createElement(Caption, _extends({
          locale: locale,
          numberOfMonths: numberOfMonths,
          startDate: startDate,
          endDate: endDate
        }, prop), /*#__PURE__*/React.createElement(MonthLabel, {
          onMonthChange: onMonthChange,
          headingTag: headingTag,
          calendarMonth: prop.calendarMonth,
          onCalendarChange: onCalendarChange,
          monthYearPicker: monthYearPicker,
          locale: locale,
          numberOfCalendar: differenceInCalendarMonths(prop.calendarMonth.date, month) + 1
        }));
      },
      Week: function Week(prop) {
        return /*#__PURE__*/React.createElement(WeekRow, prop);
      },
      Weeks: function Weeks(prop) {
        return /*#__PURE__*/React.createElement(WeekBody, prop);
      },
      Weekday: function Weekday(prop) {
        return /*#__PURE__*/React.createElement(WeekDayHeader, _extends({
          isScrollable: isScrollable
        }, prop));
      },
      Weekdays: function Weekdays(prop) {
        return /*#__PURE__*/React.createElement(WeekDaysRow, _extends({
          isScrollable: isScrollable
        }, prop));
      },
      Root: function Root(prop) {
        return /*#__PURE__*/React.createElement(RootContainer, prop);
      },
      Nav: function Nav() {
        return null;
      }
    },
    onMonthChange: onMonthChange,
    month: month
  };
  return /*#__PURE__*/React.createElement(Box, _extends({
    display: "flex",
    flexDirection: "column",
    id: id,
    ref: ref
  }, isScrollable ? {
    tabIndex: 0
  } : {}, rest), /*#__PURE__*/React.createElement("style", null, removeDefaultTableStyles), /*#__PURE__*/React.createElement(DayPicker$1, _extends({
    weekStartsOn: startingDayOfWeek,
    className: "ads-date-picker"
  }, dayPickerProps, isSingleDate ? {
    mode: 'single',
    onSelect: onDateSelect,
    selected: date
  } : {
    max: rangeMax,
    min: rangeMin,
    mode: 'range',
    onSelect: onDateRangeSelect,
    selected: dateRange,
    excludeDisabled: true
  })));
});

export { DayPicker };
//# sourceMappingURL=DayPicker.js.map
